import React from "react";
import { DocumentText } from "../../../../components/icons/svg/outline";

/**
 * TicketDescriptionSection Component
 * Reusable description/details textarea for ticket forms
 * 
 * @param {Object} data - Form data object
 * @param {Function} onChange - Callback when data changes
 * @param {boolean} readOnly - Whether the section is read-only
 * @param {string} title - Section title (default: "Description")
 * @param {React.Component} icon - Icon component to display (default: FileText)
 * @param {string} fieldName - Field name in data object (default: "description")
 * @param {string} label - Label for the textarea (default: "Description")
 * @param {string} placeholder - Placeholder text
 * @param {number} maxLength - Character limit (default: 500)
 * @param {number} rows - Number of textarea rows (default: 4)
 * @param {boolean} required - Whether field is required (default: true)
 * @returns {JSX.Element}
 */
const TicketDescriptionSection = ({
  data,
  onChange,
  readOnly = false,
  title = "Description",
  icon: Icon = DocumentText,
  fieldName = "description",
  label = "Description",
  placeholder = "Enter details...",
  maxLength = 500,
  rows = 4,
  required = true,
}) => {
  const currentLength = data?.[fieldName]?.length || 0;
  const remainingChars = maxLength - currentLength;

  return (
    <div className="space-y-4">
      {title && (
        <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-emerald-800" />}
          {title} {readOnly && "(Read Only)"}
        </h2>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && "*"}
        </label>
        {readOnly ? (
          <div className="border rounded p-4 bg-gray-50">
            <p className="text-gray-700 whitespace-pre-wrap">
              {data?.[fieldName] || "No description"}
            </p>
          </div>
        ) : (
          <>
            <textarea
              className="w-full focus:ring-[1px] focus:ring-gray-500 focus:outline-none border rounded p-2 caret-black"
              rows={rows}
              placeholder={placeholder}
              value={data?.[fieldName] || ""}
              maxLength={maxLength}
              onChange={(e) =>
                onChange?.({ ...data, [fieldName]: e.target.value })
              }
              required={required}
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {remainingChars} characters remaining
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketDescriptionSection;
